// Note: this file is an extension to utils/audience-list and will be merged with it
import type {
  AudienceRule,
  AudienceBlocks,
  AudienceRuleApi,
  AudienceBlocksApi,
  AudienceListApiResponse,
  CombinedActionAudienceRule,
} from 'src/types/audience-list';
import type {DynamicLabels} from 'src/types/dynamic-labels';
import type {NewActionTypeAudienceRule} from 'src/types/audience-revamp';

import cloneDeep from 'lodash/cloneDeep';
import uniqueId from 'lodash/uniqueId';

import {
  createRuleHash,
  createRulesHash,
  parseAudienceRule,
  ABT_ATTRIBUTE_KEY_SET,
  getDefaultInclusionBlock,
  getDefaultExclusionBlock,
  createInclusionBlockIdentifier,
  createExclusionBlockIdentifier,
  getAttributeIdentifierFromField,
  getIsZipcodeField,
  isRuleValueEmpty,
} from 'src/utils/audience-list';


export const SOURCE_TO_PATH_MAP = {
  LIST_UI: 'lists',
  CSV_LIST_UI: 'csv-lists',
} as const;
export const LISTS_PATH = '/people/lists';
export const INCLUDE_ALL_VALUE: string = 'ANY';
export const INCLUDE_ALL_OPERATOR: string = 'is_any';

export const ABT_DSL_CREATOR_HELPER = {
  action_email: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'journey'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'journeyValue'],
      operator: ['fromRule', 'journeyOperator'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_type', 'field'],
      value: ['fixed', 'touchpoint'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_id', 'field'],
      value: ['fromRule', 'touchpointValue'],
      operator: ['fromRule', 'touchpointOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_survey: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'journey'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'journeyValue'],
      operator: ['fromRule', 'journeyOperator'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_type', 'field'],
      value: ['fixed', 'touchpoint'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_id', 'field'],
      value: ['fromRule', 'touchpointValue'],
      operator: ['fromRule', 'touchpointOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_survey_question: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'touchpoint'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'touchpointValue'],
      operator: ['fromRule', 'touchpointOperator'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_type', 'field'],
      value: ['fixed', 'event_module'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_id', 'field'],
      value: ['fromRule', 'questionValue'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fromRule', 'responseAttributeName', 'field'],
      operator: ['fromRule', 'responseOperator'],
      value: ['fromRule', 'responseValue'],
    },
    {
      attribute_name: ['fixed', 'action_journey_id', 'field'],
      value: ['fromRule', 'journeyValue'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'start_date', 'field'],
      operator: ['fixed', 'gte'],
      value: ['fromRule', 'fromDate'],
    },
    {
      attribute_name: ['fixed', 'end_date', 'field'],
      operator: ['fixed', 'lte'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_external_link: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'touchpoint'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'touchpointValue'],
      operator: ['fromRule', 'touchpointOperator'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_type', 'field'],
      value: ['fixed', 'external_link'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_id', 'field'],
      value: ['fromRule', 'externalLinkValue'],
      operator: ['fromRule', 'externalLinkOperator'],
    },
    {
      attribute_name: ['fixed', 'action_journey_id', 'field'],
      value: ['fromRule', 'journeyValue'],
      operator: ['fromRule', 'journeyOperator'],
    },
    {
      attribute_name: ['fixed', 'start_date', 'field'],
      operator: ['fixed', 'gte'],
      value: ['fromRule', 'fromDate'],
    },
    {
      attribute_name: ['fixed', 'end_date', 'field'],
      operator: ['fixed', 'lte'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_email_v2: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'task_definition_id'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'taskDefinitionValue'],
      operator: ['fromRule', 'taskDefinitionOperator'],
    },
    {
      attribute_name: ['fixed', 'workflow_id', 'field'],
      value: ['fromRule', 'workflowValue'],
      operator: ['fromRule', 'workflowOperator'],
    },

    {
      attribute_name: ['fixed', 'node_id', 'field'],
      value: ['fromRule', 'nodeValue'],
      operator: ['fromRule', 'nodeOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
  chatbot_action: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'chatbot_flow_id'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'chatbotValue'],
      operator: ['fromRule', 'chatbotOperator'],
    },
    {
      attribute_name: ['fixed', 'source', 'field'],
      value: ['fromRule', 'sourceValue'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'journey_id', 'field'],
      value: ['fromRule', 'journeyValue'],
      operator: ['fromRule', 'journeyOperator'],
    },
    {
      attribute_name: ['fixed', 'touchpoint_id', 'field'],
      value: ['fromRule', 'touchpointValue'],
      operator: ['fromRule', 'touchpointOperator'],
    },

    {
      attribute_name: ['fixed', 'workflow_id', 'field'],
      value: ['fromRule', 'workflowValue'],
      operator: ['fromRule', 'workflowOperator'],
    },

    {
      attribute_name: ['fixed', 'node_id', 'field'],
      value: ['fromRule', 'nodeValue'],
      operator: ['fromRule', 'nodeOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_sms: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'workflow'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'workflowValue'],
      operator: ['fromRule', 'workflowOperator'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_type', 'field'],
      value: ['fixed', 'node'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_id', 'field'],
      value: ['fromRule', 'nodeValue'],
      operator: ['fromRule', 'nodeOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_whatsapp: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'workflow'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'workflowValue'],
      operator: ['fromRule', 'workflowOperator'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_type', 'field'],
      value: ['fixed', 'node'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_id', 'field'],
      value: ['fromRule', 'nodeValue'],
      operator: ['fromRule', 'nodeOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_smart_scheduling: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'workflow'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'workflowValue'],
      operator: ['fromRule', 'workflowOperator'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_type', 'field'],
      value: ['fixed', 'node'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_id', 'field'],
      value: ['fromRule', 'nodeValue'],
      operator: ['fromRule', 'nodeOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_meeting_templates: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'meeting_template'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'meetingTemplateValue'],
      operator: ['fromRule', 'meetingTemplateOperator'],
    },
    {
      attribute_name: ['fixed', 'workflow_id', 'field'],
      value: ['fromRule', 'workflowValue'],
      operator: ['fromRule', 'workflowOperator'],
    },

    {
      attribute_name: ['fixed', 'node_id', 'field'],
      value: ['fromRule', 'nodeValue'],
      operator: ['fromRule', 'nodeOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_voice_ai: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'voice_bot_id'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'voicebotValue'],
      operator: ['fromRule', 'voicebotOperator'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_type', 'field'],
      value: ['fixed', 'node'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'sub_reference_id', 'field'],
      value: ['fromRule', 'nodeValue'],
      operator: ['fromRule', 'nodeOperator'],
    },
    {
      attribute_name: ['fixed', 'workflow_id', 'field'],
      value: ['fromRule', 'workflowValue'],
      operator: ['fromRule', 'workflowOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_voice_inbound: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'voice_bot_id'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'voicebotValue'],
      operator: ['fromRule', 'voicebotOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_survey_v2: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'survey_schema_id'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'surveyV2Value'],
      operator: ['fromRule', 'surveyV2Operator'],
    },
    {
      attribute_name: ['fixed', 'workflow_id', 'field'],
      value: ['fromRule', 'workflowValue'],
      operator: ['fromRule', 'workflowOperator'],
    },
    {
      attribute_name: ['fixed', 'node_id', 'field'],
      value: ['fromRule', 'nodeValue'],
      operator: ['fromRule', 'nodeOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
  action_external_link_v2: [
    {
      attribute_name: ['fromRule', 'abtAttributeName', 'field'],
      from_relations: ['fromRule', 'abtRelationArray', 'field'],
      value: ['fixed', true],
    },
    {
      attribute_name: ['fixed', 'reference_type', 'field'],
      value: ['fixed', 'hash'],
      operator: ['fixed', 'eq'],
    },
    {
      attribute_name: ['fixed', 'reference_id', 'field'],
      value: ['fromRule', 'externalLinkV2Value'],
      operator: ['fromRule', 'externalLinkV2Operator'],
    },
    {
      attribute_name: ['fixed', 'workflow_id', 'field'],
      value: ['fromRule', 'workflowValue'],
      operator: ['fromRule', 'workflowOperator'],
    },
    {
      attribute_name: ['fixed', 'node_id', 'field'],
      value: ['fromRule', 'nodeValue'],
      operator: ['fromRule', 'nodeOperator'],
    },
    {
      operator: ['fixed', 'gte'],
      attribute_name: ['fixed', 'start_date', 'field'],
      value: ['fromRule', 'fromDate'],
    },
    {
      operator: ['fixed', 'lte'],
      attribute_name: ['fixed', 'end_date', 'field'],
      value: ['fromRule', 'toDate'],
    },
  ],
} as const;

export const isNewActionTypeRule = (rule: AudienceRule): boolean => {
  // @ts-expect-error - TS2339 - Property 'ruleType' does not exist on type 'AudienceRule'.
  if (rule.ruleType) {
    // @ts-expect-error - TS2339 - Property 'ruleType' does not exist on type 'AudienceRule'.
    return rule.ruleType === 'abt_rule_new';
  }
  return false;
};

export const normalizeActionRule = (
  rule: NewActionTypeAudienceRule,
): AudienceRuleApi[] | null | undefined => {
  const DSL_STRUCTURE = {
    operator: 'eq',
    value: null,
    field: {
      attribute_name: '',
      base_entity_type:
        rule.baseEntityType != null ? rule.baseEntityType : null,
      entity_type: rule.abtEntityType != null ? rule.abtEntityType : null,
      from_relations: [],
    },
    include_null: false,
  } as const;

  const rulesArray: AudienceRuleApi[] = [];

  const dslHelperArray = rule.abtEntityType
    ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly action_email: readonly [{ readonly attribute_name: readonly ["fromRule", "abtAttributeName", "field"]; readonly from_relations: readonly ["fromRule", "abtRelationArray", "field"]; readonly value: readonly ["fixed", true]; }, ... 5 more ..., { ...; }]; ... 8 more ...; readonly action_survey_v2: readonly [....'.
      ABT_DSL_CREATOR_HELPER[rule.abtEntityType]
    : [];
  if (!dslHelperArray) {
    throw new Error(
      `No DSL_CREATOR_HELPER found for abtEntityType: ${rule.abtEntityType}`,
    );
  }
  for (const helpObject of dslHelperArray) {
    const dslNode = cloneDeep(DSL_STRUCTURE);
    let shouldPushNode = true;
    for (const dslProp of Object.keys(helpObject)) {
      const [valueSource, valuePointer, objectLevel] = helpObject[dslProp];
      const value =
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'NewActionTypeAudienceRule'.
        valueSource === 'fromRule' ? rule[valuePointer] : valuePointer;
      if (value == null) {
        shouldPushNode = false;
        break;
      }
      if (objectLevel != null) {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly operator: "eq"; readonly value: null; readonly field: { readonly attribute_name: ""; readonly base_entity_type: EntityType | null; readonly entity_type: string | null; readonly from_relations: readonly []; }; readonly include_null: false; }'.
        dslNode[objectLevel][dslProp] = value;
      } else {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly operator: "eq"; readonly value: null; readonly field: { readonly attribute_name: ""; readonly base_entity_type: EntityType | null; readonly entity_type: string | null; readonly from_relations: readonly []; }; readonly include_null: false; }'.
        dslNode[dslProp] = value;
      }
    }
    if (shouldPushNode) {
      // @ts-expect-error - TS2345 - Argument of type '{ readonly operator: "eq"; readonly value: null; readonly field: { readonly attribute_name: ""; readonly base_entity_type: EntityType | null; readonly entity_type: string | null; readonly from_relations: readonly []; }; readonly include_null: false; }' is not assignable to parameter of type 'AudienceRuleApi'.
      rulesArray.push(dslNode);
    }
  }

  // Add offset to start_date node for rolling window
  if (rule.rollingWindowOffset != null) {
    const startDateNode = rulesArray.find(
      (node) => node.field.attribute_name === 'start_date',
    );
    if (startDateNode) {
      startDateNode.offset = -Math.abs(rule.rollingWindowOffset);
    }
  }

  return rulesArray;
};

const isABTRuleFromDSL = (conditions: Array<AudienceRuleApi>) => {
  const abtEntityTypeList = [
    'action_email',
    'action_email_v2',
    'action_survey',
    'action_survey_question',
    'action_external_link',
    'chatbot_action',
    'action_sms',
    'action_whatsapp',
    'action_voice_ai',
    'action_voice_inbound',
    'action_survey_v2',
    'action_external_link_v2',
    'action_meeting_templates',
    'action_smart_scheduling',
  ];
  if (conditions.length === 0) {
    return false;
  }
  const firstConditionNode = conditions[0];
  const ruleEntityType = firstConditionNode.field.entity_type;
  const result = abtEntityTypeList.includes(ruleEntityType);
  return result;
};

const getAbTRuleEntityType = (conditions: Array<AudienceRuleApi>): string => {
  const firstConditionNode = conditions[0];
  return firstConditionNode.field.entity_type;
};

export const getCombinedRuleItems = (
  attributeValue: string,
): CombinedActionAudienceRule => {
  const initialRuleObject =
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
    ABT_ATTRIBUTE_KEY_SET[attributeValue].initialRuleItems;
  return {...initialRuleObject.criteriaField, ...initialRuleObject.scopeField};
};

export const parseAudienceBlocks = (
  json: AudienceBlocksApi,
): AudienceBlocks => {
  const {all} = json;
  let {inclusions, exclusions} = json;
  inclusions = inclusions ?? {blocks: []};
  exclusions = exclusions ?? {blocks: []};

  let parsedInclusions = (inclusions.blocks ?? []).reduce((acc, block) => {
    const rules: Array<AudienceRule> = [];
    (block.rules ?? []).forEach((rule) => {
      if (isABTRuleFromDSL(rule.conditions)) {
        rules.push(parseActionRule(rule.conditions));
      } else {
        rules.push(parseAudienceRule(rule.conditions[0]));
      }
    });

    return acc.set(createInclusionBlockIdentifier(), rules);
  }, new Map());
  if (parsedInclusions.size === 0) {
    parsedInclusions = getDefaultInclusionBlock();
  }

  /**NOTE:(diwakersurya) all blocks from exclusion form only one block on the ui
   * by combining rules across all the blocks. this is done because exclusion
   * blocks have or between them and backend only supports or between blocks and
   * not rules.
   */
  const allExclusionRules = (exclusions.blocks ?? []).reduce<Array<any>>(
    (acc, block) => {
      const rules: Array<AudienceRule> = [];
      (block.rules ?? []).forEach((rule) => {
        if (isABTRuleFromDSL(rule.conditions)) {
          rules.push(parseActionRule(rule.conditions));
        } else {
          rules.push(parseAudienceRule(rule.conditions[0]));
        }
      });
      return [...acc, ...rules];
    },
    [],
  );
  let parsedExclusions = getDefaultExclusionBlock();
  if (allExclusionRules.length > 0) {
    parsedExclusions = new Map([
      [createExclusionBlockIdentifier(), allExclusionRules],
    ]);
  }

  return {all, inclusions: parsedInclusions, exclusions: parsedExclusions};
};

export const parseAudienceBlocksDiff = (
  json: AudienceBlocksApi,
): AudienceBlocks => {
  const {all} = json;
  let {inclusions, exclusions} = json;
  inclusions = inclusions ?? {blocks: []};
  exclusions = exclusions ?? {blocks: []};

  let parsedInclusions = (inclusions.blocks ?? []).reduce((acc, block) => {
    const rules: Array<AudienceRule> = [];
    (block.rules ?? []).forEach((rule) => {
      if (isABTRuleFromDSL(rule.conditions)) {
        const parsed = parseActionRule(rule.conditions);
        if (rule.changeType) {
          parsed.changeType = rule.changeType;
        }
        rules.push(parsed);
      } else {
        const parsed = parseAudienceRule(rule.conditions[0]);
        if (rule.changeType) {
          parsed.changeType = rule.changeType;
        }
        rules.push(parsed);
      }
    });

    return acc.set(createInclusionBlockIdentifier(), rules);
  }, new Map());
  if (parsedInclusions.size === 0) {
    parsedInclusions = getDefaultInclusionBlock();
  }

  /**NOTE:(diwakersurya) all blocks from exclusion form only one block on the ui
   * by combining rules across all the blocks. this is done because exclusion
   * blocks have or between them and backend only supports or between blocks and
   * not rules.
   */
  const allExclusionRules = (exclusions.blocks ?? []).reduce<Array<any>>(
    (acc, block) => {
      const rules: Array<AudienceRule> = [];
      (block.rules ?? []).forEach((rule) => {
        if (isABTRuleFromDSL(rule.conditions)) {
          const parsed = parseActionRule(rule.conditions);
          if (rule.changeType) {
            parsed.changeType = rule.changeType;
          }
          rules.push(parsed);
        } else {
          const parsed = parseAudienceRule(rule.conditions[0]);
          if (rule.changeType) {
            parsed.changeType = rule.changeType;
          }
          rules.push(parsed);
        }
      });
      return [...acc, ...rules];
    },
    [],
  );
  let parsedExclusions = getDefaultExclusionBlock();
  if (allExclusionRules.length > 0) {
    parsedExclusions = new Map([
      [createExclusionBlockIdentifier(), allExclusionRules],
    ]);
  }

  return {all, inclusions: parsedInclusions, exclusions: parsedExclusions};
};

const ABT_DSL_MANDATORY_FIELDS = [
  'baseEntityType',
  'abtEntityType',
  'abtAttributeName',
  'abtRelationArray',
  'fromDate',
  'toDate',
];
const JOURNEY_FIELDS = [
  'journeyValue',
  'journeyOperator',
  'touchpointValue',
  'touchpointOperator',
];
const WORKFLOW_FIELDS = [
  'workflowValue',
  'workflowOperator',
  'nodeValue',
  'nodeOperator',
];

const DSL_TO_STORE_CONFIG = {
  action_email: {
    mandatory: [...ABT_DSL_MANDATORY_FIELDS],
    groups: {
      journey_group: [...JOURNEY_FIELDS],
    },
    matches: [
      {
        match: {
          'field.attribute_name': [
            'email_sent',
            'email_delivered',
            'email_opened',
            'email_bounced',
          ],
        },
        store: [
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'journeyValue', value: 'value'},
          {key: 'journeyOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'sub_reference_id'},
        store: [
          {key: 'touchpointValue', value: 'value'},
          {key: 'touchpointOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_survey: {
    mandatory: [...ABT_DSL_MANDATORY_FIELDS],
    groups: {
      journey_group: [...JOURNEY_FIELDS],
    },
    matches: [
      {
        match: {'field.attribute_name': 'survey_completed'},
        store: [
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'journeyValue', value: 'value'},
          {key: 'journeyOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'sub_reference_id'},
        store: [
          {key: 'touchpointValue', value: 'value'},
          {key: 'touchpointOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_survey_question: {
    mandatory: [...ABT_DSL_MANDATORY_FIELDS],
    groups: {
      journey_group: [
        'journeyValue',
        'touchpointValue',
        'responseAttributeName',
        'responseValue',
        'responseOperator',
      ],
    },
    matches: [
      {
        match: {'field.attribute_name': 'survey_question_response'},
        store: [
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'touchpointValue', value: 'value'},
          {key: 'touchpointOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'sub_reference_id'},
        store: [{key: 'questionValue', value: 'value'}],
      },
      {
        match: {
          'field.attribute_name': [
            'survey_nps_score',
            'survey_calendar_date',
            'survey_rating_scale',
            'survey_text',
            'survey_multiple_options',
          ],
        },
        store: [
          {key: 'responseAttributeName', value: 'field.attribute_name'},
          {key: 'responseValue', value: 'value'},
          {key: 'responseOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'action_journey_id'},
        store: [{key: 'journeyValue', value: 'value'}],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_external_link: {
    mandatory: [...ABT_DSL_MANDATORY_FIELDS],
    groups: {
      journey_group: [
        'journeyValue',
        'touchpointValue',
        'externalLinkValue',
        'externalLinkOperator',
      ],
    },
    matches: [
      {
        match: {
          'field.attribute_name': [
            'external_link_sent',
            'external_link_clicked',
          ],
        },
        store: [
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'touchpointValue', value: 'value'},
          {key: 'touchpointOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'sub_reference_id'},
        store: [
          {key: 'externalLinkValue', value: 'value'},
          {key: 'externalLinkOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'action_journey_id'},
        store: [
          {key: 'journeyValue', value: 'value'},
          {key: 'journeyOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_email_v2: {
    mandatory: [...ABT_DSL_MANDATORY_FIELDS],
    groups: {
      workflow_group: [
        ...WORKFLOW_FIELDS,
        'taskDefinitionValue',
        'taskDefinitionOperator',
      ],
    },
    matches: [
      {
        match: {
          'field.attribute_name': [
            'email_sent_v2',
            'email_delivered_v2',
            'email_opened_v2',
            'email_bounced_v2',
            'email_skipped_v2',
          ],
        },
        store: [
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'taskDefinitionValue', value: 'value'},
          {key: 'taskDefinitionOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'workflow_id'},
        store: [
          {key: 'workflowValue', value: 'value'},
          {key: 'workflowOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'node_id'},
        store: [
          {key: 'nodeValue', value: 'value'},
          {key: 'nodeOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  chatbot_action: {
    mandatory: [...ABT_DSL_MANDATORY_FIELDS, 'chatbotValue', 'chatbotOperator'],
    groups: {
      journey_group: [...JOURNEY_FIELDS],
      workflow_group: [...WORKFLOW_FIELDS],
    },
    matches: [
      {
        match: {
          'field.attribute_name': [
            'conversation_initiated',
            'conversation_started',
            'conversation_completed',
          ],
        },
        store: [
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'chatbotValue', value: 'value'},
          {key: 'chatbotOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'journey_id'},
        store: [
          {key: 'journeyValue', value: 'value'},
          {key: 'journeyOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'touchpoint_id'},
        store: [
          {key: 'touchpointValue', value: 'value'},
          {key: 'touchpointOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'workflow_id'},
        store: [
          {key: 'workflowValue', value: 'value'},
          {key: 'workflowOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'node_id'},
        store: [
          {key: 'nodeValue', value: 'value'},
          {key: 'nodeOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_sms: {
    mandatory: [...ABT_DSL_MANDATORY_FIELDS],
    groups: {
      workflow_group: [...WORKFLOW_FIELDS],
    },
    matches: [
      {
        match: {
          'field.attribute_name': [
            'sms_sent',
            'sms_delivered',
            'sms_replied',
            'sms_bounced',
            'sms_skipped',
            'sms_opted_out',
          ],
        },
        store: [
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'workflowValue', value: 'value'},
          {key: 'workflowOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'sub_reference_id'},
        store: [
          {key: 'nodeValue', value: 'value'},
          {key: 'nodeOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_whatsapp: {
    mandatory: [...ABT_DSL_MANDATORY_FIELDS],
    groups: {
      workflow_group: [...WORKFLOW_FIELDS],
    },
    matches: [
      {
        match: {
          'field.attribute_name': [
            'whatsapp_sent',
            'whatsapp_delivered',
            'whatsapp_replied',
            'whatsapp_bounced',
            'whatsapp_skipped',
            'whatsapp_opted_out',
          ],
        },
        store: [
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'workflowValue', value: 'value'},
          {key: 'workflowOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'sub_reference_id'},
        store: [
          {key: 'nodeValue', value: 'value'},
          {key: 'nodeOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_smart_scheduling: {
    mandatory: [...ABT_DSL_MANDATORY_FIELDS],
    groups: {
      workflow_group: [...WORKFLOW_FIELDS],
    },
    matches: [
      {
        match: {
          'field.attribute_name': [
            'auto_schedule-meeting_scheduled',
            'auto_schedule-meeting_link_created',
            'auto_schedule-meeting_link_clicked',
          ],
        },
        store: [
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'workflowValue', value: 'value'},
          {key: 'workflowOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'sub_reference_id'},
        store: [
          {key: 'nodeValue', value: 'value'},
          {key: 'nodeOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_meeting_templates: {
    mandatory: [
      ...ABT_DSL_MANDATORY_FIELDS,
      'meetingTemplateValue',
      'meetingTemplateOperator',
    ],
    groups: {
      workflow_group: [...WORKFLOW_FIELDS],
    },
    matches: [
      {
        match: {
          'field.attribute_name': [
            'meeting_link-meeting_link_created',
            'meeting_link-meeting_scheduled',
            'meeting_link-meeting_rescheduled',
            'meeting_link-meeting_cancelled',
          ],
        },
        store: [
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'meetingTemplateValue', value: 'value'},
          {key: 'meetingTemplateOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'workflow_id'},
        store: [
          {key: 'workflowValue', value: 'value'},
          {key: 'workflowOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'node_id'},
        store: [
          {key: 'nodeValue', value: 'value'},
          {key: 'nodeOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_voice_ai: {
    mandatory: [
      ...ABT_DSL_MANDATORY_FIELDS,
      'voicebotValue',
      'voicebotOperator',
    ],
    groups: {
      workflow_group: [...WORKFLOW_FIELDS],
    },
    matches: [
      {
        match: {
          'field.attribute_name': [
            'voice_ai_call_failed',
            'voice_ai_call_started',
            'voice_ai_call_initiated',
            'voice_ai_call_completed',
            'voice_ai_call_rescheduled',
            'voice_ai_call_left_voicemail',
            'voice_ai_call_partially_completed',
          ],
        },
        store: [
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'voicebotValue', value: 'value'},
          {key: 'voicebotOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'sub_reference_id'},
        store: [
          {key: 'nodeValue', value: 'value'},
          {key: 'nodeOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'workflow_id'},
        store: [
          {key: 'workflowValue', value: 'value'},
          {key: 'workflowOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_voice_inbound: {
    mandatory: [
      ...ABT_DSL_MANDATORY_FIELDS,
      'voicebotValue',
      'voicebotOperator',
    ],
    groups: {
      voicebot_group: ['voicebotValue', 'voicebotOperator'],
    },
    matches: [
      {
        match: {
          'field.attribute_name': [
            'voice_inbound_job_seeker',
            'voice_inbound_reschedule_request',
            'voice_inbound_placement_enquiry',
            'voice_inbound_application_enquiry',
            'voice_inbound_feedback',
            'voice_inbound_availability_update',
            'voice_inbound_data_update',
            'voice_inbound_new_candidate',
            'voice_inbound_unclassified',
          ],
        },
        store: [
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'voicebotValue', value: 'value'},
          {key: 'voicebotOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_survey_v2: {
    mandatory: [
      ...ABT_DSL_MANDATORY_FIELDS,
      'surveyV2Value',
      'surveyV2Operator',
    ],
    groups: {
      workflow_group: [...WORKFLOW_FIELDS],
    },
    matches: [
      {
        match: {
          'field.attribute_name': ['survey_responded'],
        },
        store: [
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'surveyV2Value', value: 'value'},
          {key: 'surveyV2Operator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'workflow_id'},
        store: [
          {key: 'workflowValue', value: 'value'},
          {key: 'workflowOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'node_id'},
        store: [
          {key: 'nodeValue', value: 'value'},
          {key: 'nodeOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
  action_external_link_v2: {
    mandatory: [
      ...ABT_DSL_MANDATORY_FIELDS,
      'externalLinkV2Value',
      'externalLinkV2Operator',
    ],
    groups: {
      workflow_group: [...WORKFLOW_FIELDS],
    },
    matches: [
      {
        match: {
          'field.attribute_name': ['link_clicked_v2'],
        },
        store: [
          {key: 'abtEntityType', value: 'field.entity_type'},
          {key: 'baseEntityType', value: 'field.base_entity_type'},
          {key: 'abtAttributeName', value: 'field.attribute_name'},
          {key: 'abtRelationArray', value: 'field.from_relations'},
        ],
      },
      {
        match: {'field.attribute_name': 'reference_id'},
        store: [
          {key: 'externalLinkV2Value', value: 'value'},
          {key: 'externalLinkV2Operator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'workflow_id'},
        store: [
          {key: 'workflowValue', value: 'value'},
          {key: 'workflowOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'node_id'},
        store: [
          {key: 'nodeValue', value: 'value'},
          {key: 'nodeOperator', value: 'operator'},
        ],
      },
      {
        match: {'field.attribute_name': 'start_date'},
        store: [
          {key: 'fromDate', value: 'value'},
          {key: 'rollingWindowOffset', value: 'offset'},
        ],
      },
      {
        match: {'field.attribute_name': 'end_date'},
        store: [{key: 'toDate', value: 'value'}],
      },
    ],
  },
} as const;

export const parseActionRule = (
  conditions: AudienceRuleApi[],
): AudienceRule => {
  const abtEntityType = getAbTRuleEntityType(conditions);

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly action_email: { readonly mandatory: readonly string[]; readonly groups: { readonly journey_group: readonly string[]; }; readonly matches: readonly [{ readonly match: { readonly 'field.attribute_name': readonly ["email_sent", "email_delivered", "email_opened", "email_bounced"]; }; readonly store: readonly ...'.
  if (!abtEntityType || !DSL_TO_STORE_CONFIG[abtEntityType]) {
    throw new Error(`Invalid or missing abtEntityType: ${abtEntityType}`);
  }

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly action_email: { readonly mandatory: readonly string[]; readonly groups: { readonly journey_group: readonly string[]; }; readonly matches: readonly [{ readonly match: { readonly 'field.attribute_name': readonly ["email_sent", "email_delivered", "email_opened", "email_bounced"]; }; readonly store: readonly ...'.
  const {matches} = DSL_TO_STORE_CONFIG[abtEntityType];
  const store: Partial<NewActionTypeAudienceRule> = {
    id: uniqueId('rule'),
    ruleType: 'abt_rule_new',
  };
  type StoreValue = NewActionTypeAudienceRule[keyof NewActionTypeAudienceRule];
  const storeRecord = store as Record<string, StoreValue>;

  conditions.forEach((condition) => {
    // @ts-expect-error - TS7006 - Parameter 'configItem' implicitly has an 'any' type.
    matches.forEach((configItem) => {
      // Check if condition matches the `match` criteria
      const isMatch = configItem.match
        ? Object.entries(configItem.match).every(([key, value]: [any, any]) => {
            const fieldValue = key
              .split('.')
              // @ts-expect-error - TS7006 - Parameter 'o' implicitly has an 'any' type. | TS7006 - Parameter 'k' implicitly has an 'any' type.
              .reduce((o, k) => o?.[k], condition);

            // Support for array in `match` values
            if (Array.isArray(value)) {
              return value.includes(fieldValue);
            }
            return fieldValue === value;
          })
        : false;

      if (isMatch) {
        // Apply store rules
        // @ts-expect-error - TS7006 - Parameter 'storeRule' implicitly has an 'any' type.
        configItem.store.forEach((storeRule) => {
          const value = storeRule.value
            .split('.')
            // @ts-expect-error - TS7006 - Parameter 'obj' implicitly has an 'any' type. | TS7006 - Parameter 'key' implicitly has an 'any' type.
            .reduce((obj, key) => obj?.[key], condition); // Extract value from DSL

          if (Array.isArray(storeRecord[storeRule.key])) {
            storeRecord[storeRule.key] = [
              ...(storeRecord[storeRule.key] as StoreValue[]),
              ...(Array.isArray(value) ? value : [value]),
            ];
          } else {
            storeRecord[storeRule.key] = value;
          }
        });
      }
    });
  });

  // Derive scopeValue and normalize rollingWindowOffset from DSL
  if (store.rollingWindowOffset != null) {
    store.rollingWindowOffset = Math.abs(store.rollingWindowOffset);
    store.scopeValue = 'rolling_window';
  } else if (store.toDate === 'now') {
    store.scopeValue = 'specific_date_range';
  } else {
    store.scopeValue = 'custom_date_range';
  }

  return store as NewActionTypeAudienceRule as AudienceRule;
};

export const validateAudienceBlocks = (
  json: AudienceBlocks,
  maxORNOTblocks?: number | null,
  fields?: DynamicLabels,
): {
  blockHashCount: Map<string, number>;
  blockwiseRuleHashCount: Map<string, Map<string, number>>;
  duplicateBlockExists: boolean;
  duplicateRuleExistsInAnyBlock: boolean;
  invalidRuleExistsInAnyBlock: boolean;
  ruleExists: boolean;
  noRuleExists: boolean;
  doBlocksExceedMaxAllowedBlocks: boolean;
} => {
  const {all, inclusions, exclusions} = json;
  const allBlocks: Map<string, AudienceRule[]> = new Map([
    ...inclusions,
    ...exclusions,
  ]);
  // if two blocks lead to same hash due to same value for all the rule line items
  const blockHashCount: Map<string, number> = [...allBlocks.values()].reduce(
    (acc, rules) => {
      if (rules.length > 0) {
        const hash = createRulesHash(rules);
        const val = acc.get(hash);
        if (val) {
          return acc.set(hash, val + 1);
        }
        acc.set(hash, 1);
      }
      return acc;
    },
    new Map(),
  );
  //duplicate rules will have the same hash. this is a map which stores
  // no of rules having the same hash to detect duplicates
  // map (block id,map(ruleHash,count))
  const blockwiseRuleHashCount = [...allBlocks.entries()].reduce(
    (outerAccumulator, [blockId, rules]: [any, any]) => {
      outerAccumulator.set(
        blockId,
        // @ts-expect-error - TS7006 - Parameter 'acc' implicitly has an 'any' type. | TS7006 - Parameter 'rule' implicitly has an 'any' type.
        rules.reduce((acc, rule) => {
          const hash = createRuleHash(rule);
          const val = acc.get(hash);
          if (val) {
            return acc.set(hash, val + 1);
          }
          return acc.set(hash, 1);
        }, new Map()),
      );
      return outerAccumulator;
    },
    new Map(),
  );

  const duplicateBlockExists = [...blockHashCount.values()].some(
    (count) => count > 1,
  );
  const duplicateRuleExistsInAnyBlock = [
    ...blockwiseRuleHashCount.values(),
  ].some((ruleHashCount) =>
    [...ruleHashCount.values()].some((count) => count > 1),
  );

  const invalidRuleExistsInAnyBlock =
    all === false &&
    [...allBlocks.values()].some((rules) =>
      rules.some((rule) => {
        if (!isNewActionTypeRule(rule)) {
          // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
          if (typeof rule.operator === 'boolean') {
            return false;
          }
          // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
          if (!rule.operator) {
            return true;
          }
          if (isRuleValueEmpty(rule)) {
            return true;
          }
          const attributeIdentifier = getAttributeIdentifierFromField(rule);
          const isZipcodeField =
            // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'. | TS2339 - Property 'field' does not exist on type 'AudienceRule'.
            rule.field && rule.field.attribute_name && fields
              ? getIsZipcodeField(attributeIdentifier, fields)
              : false;
          if (
            isZipcodeField &&
            // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'AudienceRule'.
            Array.isArray(rule.value) &&
            // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'AudienceRule'.
            rule.value.length === 0
          ) {
            return true;
          }
          return false;
        }
        return isActionFieldsNotValid(rule);
      }),
    );

  const noRuleExists =
    all === false &&
    [...inclusions.values()].every((rules) => rules.length === 0);

  const ruleExists = ![...inclusions.values()].every(
    (rules) => rules.length === 0,
  );

  const doBlocksExceedMaxAllowedBlocks =
    maxORNOTblocks != null && all === false && inclusions.size > maxORNOTblocks;

  return {
    blockHashCount,
    blockwiseRuleHashCount,
    duplicateBlockExists,
    duplicateRuleExistsInAnyBlock,
    invalidRuleExistsInAnyBlock,
    ruleExists,
    noRuleExists,
    doBlocksExceedMaxAllowedBlocks,
  };
};

export const isActionFieldsNotValid = (rule: AudienceRule): boolean => {
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly action_email: { readonly mandatory: readonly string[]; readonly groups: { readonly journey_group: readonly string[]; }; readonly matches: readonly [{ readonly match: { readonly 'field.attribute_name': readonly ["email_sent", "email_delivered", "email_opened", "email_bounced"]; }; readonly store: readonly ...'.
  if (!rule.abtEntityType || !DSL_TO_STORE_CONFIG[rule.abtEntityType]) {
    console.error(
      `Invalid or missing abtEntityType: ${rule.abtEntityType || ''}`,
    );
    return true;
  }

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly action_email: { readonly mandatory: readonly string[]; readonly groups: { readonly journey_group: readonly string[]; }; readonly matches: readonly [{ readonly match: { readonly 'field.attribute_name': readonly ["email_sent", "email_delivered", "email_opened", "email_bounced"]; }; readonly store: readonly ...'.
  const {mandatory, groups} = DSL_TO_STORE_CONFIG[rule.abtEntityType];

  // Validate mandatory fields
  // @ts-expect-error - TS7006 - Parameter 'key' implicitly has an 'any' type.
  const isMandatoryInvalid = mandatory.some((key) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'AudienceRule'.
    const value = rule[key];
    if (
      value === null ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0)
    ) {
      console.log(`Mandatory key is missing or invalid: ${key}`);
      return true;
    }
    return false;
  });

  if (isMandatoryInvalid) {
    return true;
  }

  // Validate groups: At least one group must be fully valid
  const isAnyGroupValid = Object.values(groups).some((fields) => {
    return (
      Array.isArray(fields) &&
      fields.every((key) => {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'AudienceRule'.
        const value = typeof key === 'string' ? rule[key] : null;
        return (
          value !== null &&
          value !== undefined &&
          (!Array.isArray(value) || value.length > 0)
        );
      })
    );
  });

  if (!isAnyGroupValid) {
    console.log('No valid group found for:', rule.abtEntityType);
    return true;
  }

  // @ts-expect-error - TS7053 - dynamic store access
  if (rule.scopeValue === 'rolling_window') {
    // @ts-expect-error - TS7053 - dynamic store access
    const offset = rule.rollingWindowOffset;
    if (offset == null || offset < 1 || offset > 180) {
      return true;
    }
  }

  return false; // Rule is valid
};

export const mergeListRulesAndGetListsMap = (
  lists: AudienceListApiResponse[],
): {
  audienceBlocks: {
    all: boolean;
    inclusions: Map<string, AudienceRule[]>;
    exclusions: Map<string, AudienceRule[]>;
  };
  listRuleIdMap: Map<string, string[]>;
} => {
  const listRuleIdMap = new Map<string, string[]>();
  const mergedRules = lists.reduce(
    (acc, list) => {
      // @ts-expect-error - TS2345 - Argument of type 'AudienceBlocksApi | undefined' is not assignable to parameter of type 'AudienceBlocksApi'.
      const {inclusions, exclusions, all} = parseAudienceBlocks(list.rule);
      const ruleKeys: Array<string> = [];
      for (const block of inclusions.values()) {
        block.forEach(({id}) => ruleKeys.push(id));
      }
      for (const block of exclusions.values()) {
        block.forEach(({id}) => ruleKeys.push(id));
      }
      // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
      listRuleIdMap.set(list.id, ruleKeys);
      if (all) {
        //if any list contains all true that means all true;
        acc.all = true;
      }
      for (const [_, value] of inclusions) {
        acc.inclusions.set(createInclusionBlockIdentifier(), value);
      }

      for (const [_, value] of exclusions) {
        if (value.length > 0) {
          acc.exclusions.set(createExclusionBlockIdentifier(), value);
        }
      }
      return acc;
    },
    {all: false, inclusions: new Map(), exclusions: new Map()},
  );
  if (mergedRules.all) {
    return {
      audienceBlocks: {
        all: true,
        //empty inclusion and exclusion block is shown on the ui for include all
        inclusions: getDefaultInclusionBlock(),
        exclusions: getDefaultExclusionBlock(),
      },
      listRuleIdMap,
    };
  }
  if (mergedRules.exclusions.size === 0) {
    return {
      audienceBlocks: {
        all: false,
        inclusions: mergedRules.inclusions,
        //empty exclusion block is shown on the ui even if
        //there is none.
        exclusions: getDefaultExclusionBlock(),
      },
      //Note(Vish): ListRuleIdMap is required in genesis list builder to highlight corresponding rules for a list in the copy lists screen
      listRuleIdMap,
    };
  }

  return {
    audienceBlocks: {
      all: false,
      inclusions: mergedRules.inclusions,
      //NOTE:(diwakersurya) all exclusion blocks are combined to form one exclusion block
      exclusions: new Map([
        [
          createExclusionBlockIdentifier(),
          [...mergedRules.exclusions.values()].flat(),
        ],
      ]),
    },
    listRuleIdMap,
  };
};
